import { CssBaseline, GlobalStyles } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { endReplacedSession, isSessionReplaced } from '@/features/auth/singleSession';
import { PwaUpdatePrompt } from './PwaUpdatePrompt';
import { router } from './router';
import { SnackbarProvider } from './SnackbarProvider';
import { theme } from './theme';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        // Don't retry authorisation / validation failures.
        const code = (error as { code?: string; hint?: string })?.code;
        if (isSessionReplaced(error)) return false;
        if (code === '42501' || code === 'PGRST116' || code === '22023' || code === 'P0002') return false;
        return count < 2;
      },
    },
  },
  // Signed in on another device: sign this one out (see singleSession.ts).
  queryCache: new QueryCache({ onError: (error) => isSessionReplaced(error) && void endReplacedSession() }),
  mutationCache: new MutationCache({ onError: (error) => isSessionReplaced(error) && void endReplacedSession() }),
});

const globalStyles = (
  <GlobalStyles
    styles={(t) => ({
      '.skip-link': {
        position: 'absolute',
        left: -9999,
        top: 8,
        zIndex: 2000,
        padding: '8px 16px',
        background: (t.vars ?? t).palette.background.paper,
        borderRadius: 8,
      },
      '.skip-link:focus': { left: 8 },
      'a:focus-visible': { outline: `3px solid ${(t.vars ?? t).palette.secondary.main}`, outlineOffset: 2 },
      '@media (prefers-reduced-motion: reduce)': { '*': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' } },
    })}
  />
);

export function App() {
  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline enableColorScheme />
      {globalStyles}
      <QueryClientProvider client={queryClient}>
        <SnackbarProvider>
          <AuthProvider>
            <RouterProvider router={router} />
            <PwaUpdatePrompt />
          </AuthProvider>
        </SnackbarProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
